export const CLERK_ORG_ROLES = [
  'org:admin',
  'org:billing',
  'org:director',
  'org:team_lead',
  'org:product',
  'org:contributor',
  'org:support',
] as const;

export type ClerkOrgRole = (typeof CLERK_ORG_ROLES)[number];

export const CLERK_CUSTOM_ORG_ROLES = CLERK_ORG_ROLES.filter(
  (role) => role !== 'org:admin',
);

const CLERK_ROLE_ALIASES: Readonly<Record<string, ClerkOrgRole>> = {
  'org:member': 'org:contributor',
};

export const CAPABILITIES = [
  'activity.read',
  'activity.receipt.write',
  'commercial.refresh',
  'costs.read',
  'costs.write',
  'customers.read',
  'evidence.read',
  'health.read',
  'integration_sources.manage',
  'integrations.read',
  'mappings.read',
  'mappings.write',
  'maturity.read',
  'maturity.write',
  'org_profile.read',
  'org_profile.write',
  'people.read',
  'product_areas.read',
  'product_areas.write',
  'pull_requests.read',
  'reports.read',
  'review_queue.write',
  'reviews.read',
  'revenue.read',
  'roles.manage',
  'roles.read',
  'scan.start',
  'strategy.read',
  'sync.enqueue',
  'train.read',
  'usage.read',
  'work_items.read',
] as const;

export type Capability = (typeof CAPABILITIES)[number];

// The tier and scope vocabularies as TUPLES, not bare unions: a contract schema
// needs the runtime values, and deriving both from one place is what stops a new
// tier from compiling cleanly at a hand-copied `z.enum([...])` and then throwing
// inside a parse.
export const AUTHORIZATION_TIERS = [
  'denied',
  'summary',
  'scoped-detail',
  'full-detail',
] as const;
export type AuthorizationTier = (typeof AUTHORIZATION_TIERS)[number];

export const AUTHORIZATION_SCOPES = [
  'none',
  'self',
  'team',
  'aggregate',
  'organization',
] as const;
export type AuthorizationScope = (typeof AUTHORIZATION_SCOPES)[number];

interface PrincipalIdentity {
  readonly organizationId: string;
  readonly userId: string;
}

/**
 * Who a decision is about.
 *
 * `user`, `mcp` and `service` carry an organization ROLE and are decided against
 * `RBAC_POLICIES`. `private` carries none: it is a caller reading their OWN
 * private view — their personal workspace, or an organization their own GitHub
 * token proves they can see but they hold no membership in. There is no role to
 * look up, and modelling it as a separate kind is what stops the fallback from
 * being handed a borrowed one — see `PRIVATE_FALLBACK_GRANTS`.
 */
export type RbacPrincipal =
  | (PrincipalIdentity & {
      readonly kind: 'user' | 'service';
      readonly role: string;
    })
  | (PrincipalIdentity & {
      readonly kind: 'mcp';
      readonly role: string;
      readonly capabilityCeiling?: readonly Capability[];
    })
  | (PrincipalIdentity & { readonly kind: 'private' });

export const FACETS_BY_RESOURCE = {
  customers: ['pii', 'commercial'],
  people: ['stewardship'],
} as const;

export type Facet =
  (typeof FACETS_BY_RESOURCE)[keyof typeof FACETS_BY_RESOURCE][number];

export interface PolicyGrant {
  readonly facets?: readonly Facet[];
  readonly scope: AuthorizationScope;
  readonly tier: AuthorizationTier;
}

interface RbacPolicy {
  readonly resource: string;
  readonly operation: 'read' | 'write' | 'manage' | 'enqueue';
  readonly denialBoundary:
    | 'core'
    | 'credential'
    | 'provider'
    | 'cache'
    | 'enqueue';
  readonly grants: Readonly<Record<ClerkOrgRole, PolicyGrant>>;
}

const DENIED: PolicyGrant = { scope: 'none', tier: 'denied' };
const SUMMARY: PolicyGrant = { scope: 'organization', tier: 'summary' };
const SELF_DETAIL: PolicyGrant = { scope: 'self', tier: 'scoped-detail' };
const TEAM_DETAIL: PolicyGrant = { scope: 'team', tier: 'full-detail' };
const ORG_DETAIL: PolicyGrant = {
  scope: 'organization',
  tier: 'full-detail',
};
const AGGREGATE: PolicyGrant = { scope: 'aggregate', tier: 'summary' };
const CUSTOMER_DETAIL: PolicyGrant = {
  ...ORG_DETAIL,
  facets: FACETS_BY_RESOURCE.customers,
};
const ORG_STEWARDSHIP_DETAIL: PolicyGrant = {
  ...ORG_DETAIL,
  facets: FACETS_BY_RESOURCE.people,
};
const TEAM_STEWARDSHIP_DETAIL: PolicyGrant = {
  ...TEAM_DETAIL,
  facets: FACETS_BY_RESOURCE.people,
};
const SELF_STEWARDSHIP_DETAIL: PolicyGrant = {
  ...SELF_DETAIL,
  facets: FACETS_BY_RESOURCE.people,
};

function grants(
  overrides: Readonly<Partial<Record<ClerkOrgRole, PolicyGrant>>>,
): Readonly<Record<ClerkOrgRole, PolicyGrant>> {
  return Object.fromEntries(
    CLERK_ORG_ROLES.map((role) => [role, overrides[role] ?? DENIED]),
  ) as Record<ClerkOrgRole, PolicyGrant>;
}

// adding a role touches only this file; default is deny by construction via grants()

/**
 * Every OPERATIONAL role — deliberately not `org:billing`, which is a finance
 * role with no reason to read the engineering surfaces.
 *
 * Named for what it excludes: an earlier revision called this `NON_BILLING_DETAIL` and a
 * new capability reached for it meaning "everyone", which silently took the
 * capability away from billing. If a grant really is every role, use
 * `EVERY_ROLE_DETAIL`.
 */
const NON_BILLING_DETAIL = grants({
  'org:admin': ORG_DETAIL,
  'org:contributor': ORG_DETAIL,
  'org:director': ORG_DETAIL,
  'org:product': ORG_DETAIL,
  'org:support': ORG_DETAIL,
  'org:team_lead': ORG_DETAIL,
});

/** Every role in the workspace, billing included. */
const EVERY_ROLE_DETAIL = grants(
  Object.fromEntries(CLERK_ORG_ROLES.map((role) => [role, ORG_DETAIL])),
);

const MANAGER_WRITE = grants({
  'org:admin': ORG_DETAIL,
  'org:director': ORG_DETAIL,
  'org:team_lead': ORG_DETAIL,
});

const ADMIN_ONLY = grants({ 'org:admin': ORG_DETAIL });

function policy(
  resource: string,
  operation: RbacPolicy['operation'],
  denialBoundary: RbacPolicy['denialBoundary'],
  roleGrants: RbacPolicy['grants'],
): RbacPolicy {
  return { denialBoundary, grants: roleGrants, operation, resource };
}

export const RBAC_POLICIES: Readonly<Record<Capability, RbacPolicy>> = {
  'evidence.read': policy('scan-evidence', 'read', 'core', EVERY_ROLE_DETAIL),
  'product_areas.read': policy(
    'product-areas',
    'read',
    'core',
    EVERY_ROLE_DETAIL,
  ),
  'product_areas.write': policy('product-areas', 'write', 'core', ADMIN_ONLY),
  'activity.read': policy('activity', 'read', 'cache', NON_BILLING_DETAIL),
  'activity.receipt.write': policy(
    'activity-receipt',
    'write',
    'core',
    NON_BILLING_DETAIL,
  ),
  'commercial.refresh': policy(
    'commercial-data',
    'enqueue',
    'enqueue',
    MANAGER_WRITE,
  ),
  'costs.read': policy(
    'costs',
    'read',
    'provider',
    grants({
      'org:admin': ORG_DETAIL,
      'org:billing': ORG_DETAIL,
      'org:director': ORG_DETAIL,
      'org:product': SUMMARY,
      'org:team_lead': SUMMARY,
    }),
  ),
  'costs.write': policy('costs', 'write', 'provider', MANAGER_WRITE),
  'customers.read': policy(
    'customers',
    'read',
    'provider',
    grants({
      'org:admin': CUSTOMER_DETAIL,
      'org:contributor': SUMMARY,
      'org:director': CUSTOMER_DETAIL,
      'org:product': CUSTOMER_DETAIL,
      'org:support': CUSTOMER_DETAIL,
      'org:team_lead': CUSTOMER_DETAIL,
    }),
  ),
  'health.read': policy('health', 'read', 'cache', NON_BILLING_DETAIL),
  'integration_sources.manage': policy(
    'integration-sources',
    'manage',
    'credential',
    MANAGER_WRITE,
  ),
  'integrations.read': policy(
    'integrations',
    'read',
    'credential',
    MANAGER_WRITE,
  ),
  'mappings.read': policy('mappings', 'read', 'core', NON_BILLING_DETAIL),
  'mappings.write': policy('mappings', 'write', 'core', MANAGER_WRITE),
  // Reading the assessment is what every member of the workspace already did —
  // the route was paid-membership-only, so `org:billing` could read it and still
  // can. Recomputing spends model budget and rewrites the org's record, so that
  // half is a manager action.
  'maturity.read': policy('maturity', 'read', 'cache', EVERY_ROLE_DETAIL),
  'maturity.write': policy('maturity', 'write', 'core', MANAGER_WRITE),
  'org_profile.read': policy(
    'organization-profile',
    'read',
    'cache',
    NON_BILLING_DETAIL,
  ),
  'org_profile.write': policy(
    'organization-profile',
    'write',
    'cache',
    MANAGER_WRITE,
  ),
  'people.read': policy(
    'people',
    'read',
    'cache',
    grants({
      'org:admin': ORG_STEWARDSHIP_DETAIL,
      'org:contributor': SELF_STEWARDSHIP_DETAIL,
      'org:director': ORG_STEWARDSHIP_DETAIL,
      'org:product': SELF_STEWARDSHIP_DETAIL,
      'org:support': SELF_STEWARDSHIP_DETAIL,
      'org:team_lead': TEAM_STEWARDSHIP_DETAIL,
    }),
  ),
  'pull_requests.read': policy(
    'pull-requests',
    'read',
    'provider',
    NON_BILLING_DETAIL,
  ),
  'reports.read': policy(
    'reports',
    'read',
    'cache',
    grants({
      'org:admin': ORG_DETAIL,
      // Billing gets the organization spend report, not operational detail.
      'org:billing': SUMMARY,
      'org:contributor': SELF_DETAIL,
      'org:director': ORG_DETAIL,
      'org:product': ORG_DETAIL,
      // Support summary is the customer-status report.
      'org:support': SUMMARY,
      'org:team_lead': TEAM_DETAIL,
    }),
  ),
  'review_queue.write': policy(
    'review-queue',
    'write',
    'enqueue',
    NON_BILLING_DETAIL,
  ),
  'reviews.read': policy('reviews', 'read', 'provider', NON_BILLING_DETAIL),
  'revenue.read': policy(
    'revenue',
    'read',
    'provider',
    grants({
      'org:admin': ORG_DETAIL,
      'org:billing': ORG_DETAIL,
      'org:director': ORG_DETAIL,
      'org:product': SUMMARY,
      'org:team_lead': SUMMARY,
    }),
  ),
  // Reading who holds which role and CHANGING one are different authorities.
  // Managers may audit the grant table; only an admin may write to it, which is
  // also the authority Clerk itself requires for a membership update.
  'roles.manage': policy('roles', 'manage', 'core', ADMIN_ONLY),
  'roles.read': policy('roles', 'read', 'core', MANAGER_WRITE),
  'scan.start': policy('repository-scan', 'enqueue', 'enqueue', ADMIN_ONLY),
  'strategy.read': policy(
    'strategy',
    'read',
    'cache',
    grants({
      'org:admin': ORG_DETAIL,
      'org:director': ORG_DETAIL,
      'org:product': SUMMARY,
      'org:team_lead': SUMMARY,
    }),
  ),
  'sync.enqueue': policy(
    'account-sync',
    'enqueue',
    'enqueue',
    NON_BILLING_DETAIL,
  ),
  'train.read': policy(
    'competence-assessments',
    'read',
    'cache',
    grants({
      'org:admin': AGGREGATE,
      'org:contributor': SELF_DETAIL,
      'org:director': AGGREGATE,
      'org:product': SELF_DETAIL,
      'org:support': SELF_DETAIL,
      'org:team_lead': AGGREGATE,
    }),
  ),
  'usage.read': policy(
    'usage',
    'read',
    'cache',
    grants({
      'org:admin': ORG_DETAIL,
      'org:billing': ORG_DETAIL,
      'org:director': ORG_DETAIL,
      'org:team_lead': ORG_DETAIL,
    }),
  ),
  'work_items.read': policy(
    'work-items',
    'read',
    'provider',
    NON_BILLING_DETAIL,
  ),
};

/**
 * What a caller may read in their own private view.
 *
 * These are the two surfaces that shipped a personal / PAT-only fallback
 * (#2835/#2836) before RBAC existed, and the fallback is preserved rather than
 * migrated away: the caller reads their personal workspace, or an organization
 * their own token already proves they can see, from their own credentials and
 * against their own personal plan — `resolveCommercialWorkspaceAccess` never lets
 * somebody else's paid workspace sponsor it, and a private scope never mints an
 * org-scoped Convex token, so no tenant cache is read or written.
 *
 * Every other capability is absent, which means denied: a private principal
 * cannot reach roles, costs, mappings or any write. This map — not a borrowed
 * organization role — is its whole authority, which is why it cannot widen as
 * the policy grows.
 */
const PRIVATE_FALLBACK_GRANTS: Readonly<
  Partial<Record<Capability, PolicyGrant>>
> = {
  'people.read': ORG_STEWARDSHIP_DETAIL,
  'revenue.read': ORG_DETAIL,
};

/** Every capability a private-fallback principal can hold, for probes and docs. */
export const PRIVATE_FALLBACK_CAPABILITIES: readonly Capability[] =
  CAPABILITIES.filter(
    (capability) => PRIVATE_FALLBACK_GRANTS[capability] !== undefined,
  );

export interface AuthorizationDecision extends PolicyGrant {
  readonly capability: Capability | null;
  readonly reason?: 'invalid_capability' | 'key_ceiling' | 'unknown_role';
}

/**
 * Why a principal was refused. `no_principal` is the pre-policy case (the caller
 * has no membership in the addressed organization at all), `not_granted` is the
 * policy case (a recognised role whose grant for this capability is `denied`).
 */
export type PermissionDenialReason =
  | 'invalid_capability'
  | 'key_ceiling'
  | 'no_principal'
  | 'not_granted'
  | 'unknown_role';

/**
 * The user-facing denied state (#2851). It names the roles that DO hold the
 * capability as data, never as prose, so a permission notice, a nav lock reason,
 * and an API body all render the same fact without re-deriving it.
 */
export interface PermissionState {
  readonly capability: Capability | null;
  readonly grantingRoles: readonly ClerkOrgRole[];
  readonly reason: PermissionDenialReason;
}

/** Every fixed role whose grant for `capability` is not `denied`. */
export function grantingRolesFor(
  capability: Capability,
): readonly ClerkOrgRole[] {
  const { grants: roleGrants } = RBAC_POLICIES[capability];
  return CLERK_ORG_ROLES.filter((role) => roleGrants[role].tier !== 'denied');
}

/** Every fixed role whose grant for `capability` reaches exactly `tier`. */
export function rolesAtTierFor(
  capability: Capability,
  tier: AuthorizationTier,
): readonly ClerkOrgRole[] {
  const { grants: roleGrants } = RBAC_POLICIES[capability];
  return CLERK_ORG_ROLES.filter((role) => roleGrants[role].tier === tier);
}

/** Render a refused decision as the shared permission state. */
export function permissionStateFor(
  decision: Readonly<AuthorizationDecision>,
): PermissionState {
  return {
    capability: decision.capability,
    grantingRoles:
      decision.capability === null ? [] : grantingRolesFor(decision.capability),
    reason: decision.reason ?? 'not_granted',
  };
}

/** The refusal for a caller with no membership in the addressed organization. */
export function missingPrincipalState(capability: Capability): PermissionState {
  return {
    capability,
    grantingRoles: grantingRolesFor(capability),
    reason: 'no_principal',
  };
}

export type RbacRoleResolution =
  | { readonly role: ClerkOrgRole; readonly status: 'resolved' }
  | { readonly reason: 'unknown_role'; readonly status: 'denied' };

export function resolveRbacRole(clerkRole: unknown): RbacRoleResolution {
  if (typeof clerkRole !== 'string') {
    return { reason: 'unknown_role', status: 'denied' };
  }
  const resolvedRole = CLERK_ROLE_ALIASES[clerkRole] ?? clerkRole;
  if (!CLERK_ORG_ROLES.includes(resolvedRole as ClerkOrgRole)) {
    return { reason: 'unknown_role', status: 'denied' };
  }
  return { role: resolvedRole as ClerkOrgRole, status: 'resolved' };
}

function isCapability(value: unknown): value is Capability {
  return (
    typeof value === 'string' && CAPABILITIES.includes(value as Capability)
  );
}

export function authorizeCapability(
  principal: Readonly<RbacPrincipal>,
  capability: unknown,
): AuthorizationDecision {
  if (!isCapability(capability)) {
    return {
      capability: null,
      reason: 'invalid_capability',
      scope: 'none',
      tier: 'denied',
    };
  }
  if (principal.kind === 'private') {
    return { capability, ...(PRIVATE_FALLBACK_GRANTS[capability] ?? DENIED) };
  }
  const roleResolution = resolveRbacRole(principal.role);
  if (roleResolution.status === 'denied') {
    return { capability, reason: roleResolution.reason, ...DENIED };
  }
  if (
    principal.kind === 'mcp' &&
    principal.capabilityCeiling !== undefined &&
    !principal.capabilityCeiling.includes(capability)
  ) {
    return { capability, reason: 'key_ceiling', ...DENIED };
  }
  // Service principals deliberately use an explicit current role row. They do
  // not bypass role grants or facet redaction; only MCP principals add a key
  // ceiling, intersected with the owner's current role at every call.
  return {
    capability,
    ...RBAC_POLICIES[capability].grants[roleResolution.role],
  };
}
